import { RefObject, useEffect, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';

import { KimErrorState } from './kim-error-state';
import { ScrollToBottomButton } from './scroll-to-bottom-button';
import { SurveyBlock as SurveyBlockComponent } from './survey-block';
import { VerifyDisclaimer } from './verify-disclaimer';
import { KimThreadStatus } from '../../../../../../../../../../../backend/src/matter/subset/thread/kim-thread-status';
import { KimThread } from '../../../../../../../../../../../backend/src/matter/subset/thread/kim-thread.entity';
import { Permission } from '../../../../../../../../../../../backend/src/shared/action-registry';
import { SurveyBlock } from '../../../../../../../../../../../backend/src/types/types';
import { KimActionsMenu } from '../../components/kim-actions-menu';
import { KIM_INPUT_CHARACTER_LIMIT, KimInput } from '../../components/kim-input';
import { useKimFileUpload } from '../../hooks/use-kim-file-upload';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useSendKimInterrupt } from '@/api/mutations/useSendKimInterrupt';
import { useSendKimMessage } from '@/api/mutations/useSendKimMessage';
import { resetNewThreadWarmThrottle, useWarmKim } from '@/api/mutations/useWarmKim';
import { useUserContext } from '@/Contexts/User';
import { useHasPermission } from '@/hooks/useHasPermission';
import { DocMentionsInputHandle, extractKimMentions } from '@/new/components/doc-mentions-input';
import { Icon } from '@/new/components/icon';

// Owns all input/footer state (typed text, uploads, interrupts, survey dismissal) so
// keystrokes and upload progress never re-render the message list above it.
export function KimComposer({
  matterId,
  subsetId,
  threadId,
  thread,
  activeSurvey,
  isAgentBusy,
  isComposing,
  isThreadActive,
  isThreadSettled,
  disabled,
  onSendingChange,
  scrollContainerRef,
}: {
  matterId?: string;
  subsetId?: string;
  threadId?: string;
  thread?: KimThread;
  activeSurvey: SurveyBlock | null;
  isAgentBusy: boolean;
  isComposing: boolean;
  isThreadActive: boolean;
  isThreadSettled: boolean;
  /** Blocks the input entirely (thread missing/failed or messages not loaded). */
  disabled: boolean;
  /** Reports the send mutation's in-flight state up to the page (rare flips). */
  onSendingChange: (isSending: boolean) => void;
  scrollContainerRef: RefObject<HTMLDivElement>;
}) {
  const { user } = useUserContext();
  const kimInputRef = useRef<DocMentionsInputHandle>(null);
  const [inputValue, setInputValue] = useState('');
  const { mutate: sendMessage, isLoading: isSending } = useSendKimMessage(matterId, subsetId, threadId);
  const { mutate: sendInterrupt } = useSendKimInterrupt(matterId, subsetId, threadId);
  const { uploadedFiles, isUploading, addFiles, removeFile, clearFiles } = useKimFileUpload(matterId);

  const warmKim = useWarmKim(matterId, subsetId, threadId);
  useEffect(() => {
    warmKim();
  }, [warmKim]);
  const [dismissedSurveyIds, setDismissedSurveyIds] = useState<Set<string>>(new Set());
  const [optimisticInterrupt, setOptimisticInterrupt] = useState(false);

  useEffect(() => {
    onSendingChange(isSending);
  }, [isSending, onSendingChange]);

  // Focus the input when an AI answer finishes streaming
  const prevAgentBusyRef = useRef(isAgentBusy);
  useEffect(() => {
    if (prevAgentBusyRef.current && !isAgentBusy) {
      kimInputRef.current?.focus();
    }
    prevAgentBusyRef.current = isAgentBusy;
  }, [isAgentBusy]);

  // Clear any pending interrupt state when switching threads
  useEffect(() => {
    setOptimisticInterrupt(false);
  }, [threadId]);

  useEffect(() => {
    if (isThreadSettled) setOptimisticInterrupt(false);
  }, [isThreadSettled]);

  // Sending is gated on the stream alone (same check the release branch uses):
  // isAgentBusy holds until the run-terminal EndOfStreamBlock arrives, with the
  // DB fallback initialising the state after a reload. Thread status is not
  // consulted here — it lags behind the stream by up to one poll cycle and is
  // only authoritative for the stop button (isThreadActive) and recovery.
  const sendDisabled = isSending || isAgentBusy || isComposing;

  const handleSurveySubmit = (formatted: string) => {
    if (sendDisabled || isUploading) return;
    setOptimisticInterrupt(false);

    sendMessage(
      { message: formatted },
      {
        onSuccess: () => {
          resetNewThreadWarmThrottle();
          trackCustomEvent(TrackingEventsV2.KimSendMessage, { matterId, subsetId, threadId });
        },
      },
    );
  };

  const handleSendMessage = () => {
    const text = inputValue.trim();
    if (!text || text.length > KIM_INPUT_CHARACTER_LIMIT || isUploading || sendDisabled) return;

    const files = uploadedFiles.length > 0 ? uploadedFiles : undefined;
    setOptimisticInterrupt(false);

    sendMessage(
      { message: text, files, mentions: extractKimMentions(text) },
      {
        onSuccess: () => {
          resetNewThreadWarmThrottle();
          trackCustomEvent(TrackingEventsV2.KimSendMessage, { matterId, subsetId, threadId });
          setInputValue('');
          clearFiles();
        },
      },
    );
  };

  const handleInterrupt = () => {
    setOptimisticInterrupt(true);
    sendInterrupt(undefined, { onError: () => setOptimisticInterrupt(false) });
  };

  const { hasPermission } = useHasPermission();
  const canCreateThread = hasPermission(Permission.ThreadCreate);

  const showSurvey = activeSurvey !== null && !dismissedSurveyIds.has(activeSurvey.id);
  const isSharedThread = Boolean(thread && user && thread.createdById !== user.id);

  return (
    <AnimatePresence initial={false}>
      <div className="relative -m-1 mx-auto w-full max-w-3xl p-1 pt-0">
        <div className="pointer-events-none absolute bottom-full flex w-full flex-col items-center pr-2">
          <ScrollToBottomButton scrollContainerRef={scrollContainerRef} threadId={threadId} />

          {thread?.status === KimThreadStatus.Failed && (
            <motion.div
              key="error-state"
              initial={{ opacity: 0, filter: 'blur(2px)' }}
              animate={{ opacity: 1, filter: 'blur(0px)' }}
              exit={{ opacity: 0, filter: 'blur(2px)' }}
              transition={{ duration: 0.15 }}
              className="mb-2 w-full"
            >
              <KimErrorState />
            </motion.div>
          )}

          {thread?.isLegacy && (
            <motion.div
              key="legacy-thread"
              initial={{ opacity: 0, filter: 'blur(2px)' }}
              animate={{ opacity: 1, filter: 'blur(0px)' }}
              exit={{ opacity: 0, filter: 'blur(2px)' }}
              transition={{ duration: 0.15 }}
              className="flex w-full gap-2.5 rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-500"
            >
              <Icon className="lucide--history mt-0.75" />
              <span>
                This is a legacy thread from the previous version of Kim. It's read-only and can't be continued.
              </span>
            </motion.div>
          )}

          {!thread?.isLegacy && isSharedThread && (
            <motion.div
              key="cannot-message"
              initial={{ opacity: 0, filter: 'blur(2px)' }}
              animate={{ opacity: 1, filter: 'blur(0px)' }}
              exit={{ opacity: 0, filter: 'blur(2px)' }}
              transition={{ duration: 0.15 }}
              className="flex w-full gap-2.5 rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-500"
            >
              <Icon className="lucide--info mt-0.75" />
              <span>You can't send messages to a thread created by another user.</span>
            </motion.div>
          )}
        </div>
        {!thread?.isLegacy &&
          !isSharedThread &&
          (canCreateThread ? (
            showSurvey && activeSurvey ? (
              <motion.div
                key={`survey-${activeSurvey.id}`}
                initial={{ opacity: 0, filter: 'blur(2px)' }}
                animate={{ opacity: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, filter: 'blur(2px)' }}
                transition={{ duration: 0.15 }}
              >
                <SurveyBlockComponent
                  questions={activeSurvey.questions}
                  onSubmit={handleSurveySubmit}
                  onDismiss={() =>
                    setDismissedSurveyIds((prev) => {
                      const next = new Set(prev);
                      next.add(activeSurvey.id);
                      return next;
                    })
                  }
                  disabled={sendDisabled || isUploading}
                />
              </motion.div>
            ) : (
              <motion.div
                key="kim-input"
                initial={{ opacity: 0, filter: 'blur(2px)' }}
                animate={{ opacity: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, filter: 'blur(2px)' }}
                transition={{ duration: 0.15 }}
              >
                <KimInput
                  ref={kimInputRef}
                  threadId={threadId}
                  className="max-h-100 min-h-16 p-4 py-3 pb-0"
                  placeholder="Ask Kim..."
                  value={inputValue}
                  onChange={setInputValue}
                  onSubmit={handleSendMessage}
                  onInterrupt={handleInterrupt}
                  isGenerating={isThreadActive && isAgentBusy && !isSending && !optimisticInterrupt}
                  isInterrupting={optimisticInterrupt && isAgentBusy}
                  sendDisabled={sendDisabled}
                  disabled={disabled}
                  uploadedFiles={uploadedFiles}
                  isUploading={isUploading}
                  onFilesAdded={addFiles}
                  onFileRemove={removeFile}
                  actions={
                    <KimActionsMenu
                      inputRef={kimInputRef}
                      onFilesAdded={addFiles}
                      onTemplateSelect={setInputValue}
                      isUploading={isUploading}
                      disabled={disabled}
                    />
                  }
                />
              </motion.div>
            )
          ) : (
            <motion.div
              key="no-perms-message"
              initial={{ opacity: 0, filter: 'blur(2px)' }}
              animate={{ opacity: 1, filter: 'blur(0px)' }}
              exit={{ opacity: 0, filter: 'blur(2px)' }}
              transition={{ duration: 0.15 }}
              className="flex w-full gap-2.5 rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-500"
            >
              <Icon className="lucide--lock mt-0.75" />
              <span>You don't have permission to send messages. Speak to an administrator to gain access.</span>
            </motion.div>
          ))}
        <VerifyDisclaimer />
      </div>
    </AnimatePresence>
  );
}
